import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgePoundSterling,
  Banknote,
  CalendarCheck,
  MapPin,
  ShieldCheck,
  Moon,
  Sun,
} from "lucide-react";
import Seo from "../../components/Seo";
import DisclaimerBand from "../../components/DisclaimerBand";
import { SITE } from "../../config/site";

/**
 * RoutePage — shared layout for the Essex ↔ London route pages.
 * All copy is supplied per route via props; no invented facts.
 */
export default function RoutePage({
  title,
  description,
  eyebrow = "Essex ↔ London",
  h1,
  intro,
  areas,
  londonAreas,
  morningText,
  eveningText,
  note,
  noteTitle = "Good to know",
}) {
  return (
    <main data-testid="route-page">
      <Seo title={title} description={description} />

      {/* HERO */}
      <section className="border-b border-[#27272A]">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">{eyebrow}</p>
          <h1 className="mt-4 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            {h1}
          </h1>
          <p className="mt-5 max-w-2xl leading-relaxed text-[#A1A1AA]">{intro}</p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <Link
              to="/book"
              data-testid="route-book-cta"
              className="focus-gold inline-flex items-center justify-center gap-2 border border-[#D4AF37] bg-[#D4AF37] px-8 py-4 text-sm font-semibold uppercase tracking-wider text-black transition-colors duration-150 hover:bg-[#E5C158]"
            >
              Request a £100 Journey <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link
              to="/areas-covered"
              data-testid="route-areas-link"
              className="focus-gold inline-flex items-center justify-center gap-2 border border-[#D4AF37] px-8 py-4 text-sm font-semibold uppercase tracking-wider text-[#D4AF37] transition-colors duration-150 hover:bg-[#D4AF37] hover:text-black"
            >
              All areas covered
            </Link>
          </div>
        </div>
      </section>

      {/* AREAS */}
      <section className="section-pad mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="border border-[#27272A] bg-[#111111] p-8" data-testid="route-essex-areas">
            <div className="flex items-center gap-3">
              <MapPin className="h-6 w-6 text-[#D4AF37]" aria-hidden="true" />
              <h2 className="text-xl font-semibold text-white">Essex pickups on this route</h2>
            </div>
            <ul className="mt-5 space-y-3">
              {areas.map((a) => (
                <li key={a} className="flex items-center gap-3 text-[#D4D4D8]">
                  <span className="h-px w-6 bg-[#D4AF37]" aria-hidden="true" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-[#27272A] bg-[#111111] p-8" data-testid="route-london-areas">
            <div className="flex items-center gap-3">
              <MapPin className="h-6 w-6 text-[#D4AF37]" aria-hidden="true" />
              <h2 className="text-xl font-semibold text-white">London drop-offs &amp; pickups</h2>
            </div>
            <ul className="mt-5 space-y-3">
              {londonAreas.map((a) => (
                <li key={a} className="flex items-center gap-3 text-[#D4D4D8]">
                  <span className="h-px w-6 bg-[#D4AF37]" aria-hidden="true" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* DIRECTIONS */}
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div className="border border-[#27272A] bg-[#111111] p-8" data-testid="route-outbound-card">
            <Sun className="h-7 w-7 text-[#D4AF37]" aria-hidden="true" />
            <h2 className="mt-4 text-xl font-semibold text-white">Into London</h2>
            <p className="mt-2 text-sm leading-relaxed text-[#A1A1AA]">{morningText}</p>
          </div>
          <div className="border border-[#27272A] bg-[#111111] p-8" data-testid="route-return-card">
            <Moon className="h-7 w-7 text-[#D4AF37]" aria-hidden="true" />
            <h2 className="mt-4 text-xl font-semibold text-white">Return journeys from London</h2>
            <p className="mt-2 text-sm leading-relaxed text-[#A1A1AA]">{eveningText}</p>
          </div>
        </div>

        {note && (
          <div
            className="mt-6 flex items-start gap-4 border border-[rgba(212,175,55,0.35)] bg-[#0B0B0B] p-6"
            data-testid="route-note"
          >
            <CalendarCheck className="mt-0.5 h-6 w-6 shrink-0 text-[#D4AF37]" aria-hidden="true" />
            <div>
              <h2 className="text-lg font-semibold text-white">{noteTitle}</h2>
              <p className="mt-1 text-sm leading-relaxed text-[#A1A1AA]">{note}</p>
            </div>
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section className="border-y border-[#27272A] bg-[#0B0B0B]">
        <div className="section-pad mx-auto max-w-7xl px-4 sm:px-6">
          <h2 className="text-3xl font-semibold tracking-tight text-white">How booking works</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: CalendarCheck,
                title: "Request your journey",
                text: "Send your pickup, destination, date and time. Your request is reviewed personally — nothing is automatic.",
              },
              {
                icon: ShieldCheck,
                title: "We confirm a driver",
                text: "We check our TfL-licensed London Black Cab drivers' travel plans and contact you to confirm. A request is not a booking until we do.",
              },
              {
                icon: Banknote,
                title: "Pay in the cab",
                text: "Pay your driver the £100 fixed fare by cash or card inside the cab at the end of the journey. No advance payment, ever.",
              },
            ].map((s) => (
              <div key={s.title} className="border border-[#27272A] bg-[#111111] p-8">
                <s.icon className="h-7 w-7 text-[#D4AF37]" aria-hidden="true" />
                <h3 className="mt-4 text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#A1A1AA]">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad mx-auto max-w-4xl px-4 text-center sm:px-6">
        <BadgePoundSterling className="mx-auto h-10 w-10 text-[#D4AF37]" aria-hidden="true" />
        <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
          £100 fixed fare, <span className="text-[#D4AF37]">paid in the cab</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl leading-relaxed text-[#A1A1AA]">
          Send your journey request and we will check whether one of our Essex-based London Black
          Cab drivers is already travelling your way. {SITE.availabilityMessage}
        </p>
        <Link
          to="/book"
          data-testid="route-bottom-cta"
          className="focus-gold mt-8 inline-flex items-center gap-2 border border-[#D4AF37] bg-[#D4AF37] px-8 py-4 text-sm font-semibold uppercase tracking-wider text-black transition-colors duration-150 hover:bg-[#E5C158]"
        >
          Request a £100 Journey <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
        <div className="mx-auto mt-10 max-w-2xl text-left">
          <DisclaimerBand />
        </div>
      </section>
    </main>
  );
}
